import { useState, useCallback } from 'react';
import { Heart, Send, Share2, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { lc } from '@/i18n/copy';
import type { Language } from '@/i18n/types';
import type { SavedItem } from '@/pages/SavedMessages';
import { detectCategory } from '@/pages/SavedMessages';

const copy = {
  en: { takeIn: 'Take this in again.', feelAgain: 'Feel this again', sendLike: 'Send something like this', shareMoment: 'Share this moment', of: 'of', from: 'From', anonymous: 'Someone' },
  es: { takeIn: 'Vuelve a sentir esto.', feelAgain: 'Siente esto de nuevo', sendLike: 'Envía algo así', shareMoment: 'Comparte este momento', of: 'de', from: 'De', anonymous: 'Alguien' },
  fr: { takeIn: 'Prenez le temps de ressentir.', feelAgain: 'Ressentir à nouveau', sendLike: 'Envoyer quelque chose comme ça', shareMoment: 'Partager ce moment', of: 'de', from: 'De', anonymous: 'Quelqu\'un' },
  pt: { takeIn: 'Sinta isso de novo.', feelAgain: 'Sentir novamente', sendLike: 'Enviar algo assim', shareMoment: 'Compartilhar este momento', of: 'de', from: 'De', anonymous: 'Alguém' },
  ar: { takeIn: 'استشعر هذا مجدداً.', feelAgain: 'اشعر بهذا مجدداً', sendLike: 'أرسل شيئاً مشابهاً', shareMoment: 'شارك هذه اللحظة', of: 'من', from: 'من', anonymous: 'شخص ما' },
  ja: { takeIn: 'もう一度感じてください。', feelAgain: 'もう一度感じる', sendLike: 'こんなメッセージを送る', shareMoment: 'この瞬間を共有', of: '/', from: '送信者', anonymous: '誰か' },
  ko: { takeIn: '다시 한번 느껴보세요.', feelAgain: '다시 느끼기', sendLike: '이런 메시지 보내기', shareMoment: '이 순간 공유', of: '/', from: '보낸 사람', anonymous: '누군가' },
  id: { takeIn: 'Rasakan lagi.', feelAgain: 'Rasakan lagi', sendLike: 'Kirim sesuatu seperti ini', shareMoment: 'Bagikan momen ini', of: 'dari', from: 'Dari', anonymous: 'Seseorang' },
  hi: { takeIn: 'इसे फिर से महसूस करें।', feelAgain: 'फिर से महसूस करें', sendLike: 'ऐसा कुछ भेजें', shareMoment: 'यह पल साझा करें', of: 'में से', from: 'भेजने वाला', anonymous: 'कोई' },
};

const GRADIENT_OVERLAYS: Record<string, string> = {
  classic: 'from-purple-950 via-indigo-950 to-purple-950',
  warmth: 'from-amber-950 via-orange-950 to-amber-950',
  forever: 'from-rose-950 via-pink-950 to-rose-950',
  courage: 'from-teal-950 via-emerald-950 to-teal-950',
  gratitude: 'from-green-950 via-emerald-950 to-green-950',
  celebration: 'from-yellow-950 via-amber-950 to-yellow-950',
  legacy: 'from-slate-950 via-zinc-950 to-slate-950',
  custom: 'from-violet-950 via-purple-950 to-violet-950',
};

interface Props {
  items: SavedItem[];
  language: Language;
  onClose: () => void;
  onSendAgain: (item: SavedItem) => void;
  onShare: (item: SavedItem) => void;
  startIndex?: number;
}

export function RevisitMode({ items, language, onClose, onSendAgain, onShare, startIndex = 0 }: Props) {
  const c = lc(copy, language);
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [direction, setDirection] = useState(0);

  const item = items[currentIndex];
  if (!item) return null;

  const template = item.announcement?.template || 'classic';
  const gradientBg = GRADIENT_OVERLAYS[template] || GRADIENT_OVERLAYS.classic;

  const goNext = () => {
    if (currentIndex < items.length - 1) {
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  };

  const getSenderLabel = () => {
    if (!item.announcement) return '';
    if (item.announcement.is_anonymous) return c.anonymous;
    return item.announcement.sender_display_name || c.anonymous;
  };

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Background */}
      <div className={cn("absolute inset-0 bg-gradient-to-b", gradientBg)} />
      <div className="absolute inset-0 bg-black/40" />

      {/* Close button */}
      <div className="relative z-10 flex items-center justify-between px-4 pt-4 pb-2">
        <button onClick={onClose} className="p-2 text-white/50 hover:text-white/80 transition-colors">
          <X className="h-5 w-5" />
        </button>
        <span className="text-[12px] text-white/65 font-medium">
          {currentIndex + 1} {c.of} {items.length}
        </span>
        <div className="w-9" />
      </div>

      {/* Top text */}
      <motion.p
        key={`header-${currentIndex}`}
        className="relative z-10 text-center text-[13px] text-white/70 italic px-8 mb-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        {c.takeIn}
      </motion.p>

      {/* Message content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={item.id}
            custom={direction}
            initial={{ opacity: 0, x: direction * 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -60 }}
            transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
            className="w-full max-w-lg"
          >
            {/* Recipient */}
            <motion.p
              className="text-center text-white/50 text-[13px] mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {item.announcement?.recipient_name && (
                <span className="font-semibold text-white/70">
                  {item.announcement.recipient_name}
                </span>
              )}
            </motion.p>

            {/* Message */}
            <motion.div
              className="relative px-2"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8, ease: [0.22, 0.61, 0.36, 1] }}
            >
              <p className="text-white/90 text-[17px] leading-[1.7] text-center font-light">
                {item.announcement?.message || ''}
              </p>
            </motion.div>

            {/* Sender */}
            <motion.p
              className="text-center text-[12px] text-white/65 italic mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              {c.from} {getSenderLabel()}
            </motion.p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation arrows */}
      <div className="relative z-10 flex items-center justify-between px-4 py-2">
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className={cn("p-3 rounded-full transition-all", currentIndex === 0 ? "opacity-0" : "text-white/40 hover:text-white/70 hover:bg-white/5")}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={goNext}
          disabled={currentIndex === items.length - 1}
          className={cn("p-3 rounded-full transition-all", currentIndex === items.length - 1 ? "opacity-0" : "text-white/40 hover:text-white/70 hover:bg-white/5")}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Action buttons */}
      <div className="relative z-10 flex items-center justify-center gap-3 px-4 pb-8 pt-2">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-[12px] text-white/60 hover:text-white/90 hover:bg-white/10 transition-all active:scale-[0.96]"
        >
          <Heart className="h-3.5 w-3.5" /> {c.feelAgain}
        </button>
        <button
          onClick={() => onSendAgain(item)}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/10 border border-white/15 text-[12px] text-white/70 hover:text-white hover:bg-white/15 transition-all active:scale-[0.96]"
        >
          <Send className="h-3.5 w-3.5" /> {c.sendLike}
        </button>
        <button
          onClick={() => onShare(item)}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-[12px] text-white/60 hover:text-white/90 hover:bg-white/10 transition-all active:scale-[0.96]"
        >
          <Share2 className="h-3.5 w-3.5" /> {c.shareMoment}
        </button>
      </div>
    </motion.div>
  );
}
